import { IFramework, IWebServiceManager, HttpMethod, IUtilities } from '@truckdown/framework';
import { ILogData } from './ILogData';
import { LogEntryModel } from "./LogEntryModel";
import { LogEntryQuery } from "./LogEntryQuery";
import { ResultModel } from "./ResultModel";
import { SystemInfoModel } from "./SystemInfoModel";
import { SystemInfoQuery } from "./SystemInfoQuery";
import { UserContextModel } from "./UserContextModel";
import { UserContextQuery } from "./UserContextQuery";
import { LogLevel } from './LogLevel';

enum LogLevelForQuery {
    Trace,
    Debug,
    Information,
    Warning,
    Error,
    Critical,
    None
}

class LogData implements ILogData {
    constructor(framework: IFramework) {
        this.manager = framework.getRequiredService<IWebServiceManager>('IWebServiceManager');
        this.utilities = framework.getRequiredService<IUtilities>('IUtilities');
    }

    readonly manager: IWebServiceManager;
    readonly utilities: IUtilities;

    getActiveInstances(): Promise<SystemInfoModel[]> {
        let request = this.manager.createRequest({
            service: this.manager.api,
            path: '/logging/v3/instance/active',
            method: HttpMethod.GET
        });

        return new Promise<SystemInfoModel[]>((resolve, reject) => {
            this.manager
                .get<any[]>(request)
                .then((res) => {
                    if (res && res !== null && this.utilities.data.isArray(res)) {
                        let result = [];
                        for (var i = 0; i < res.length; i++) {
                            result.push(new SystemInfoModel(res[i]));
                        }
                        resolve(result);
                    } else {
                        resolve([]);
                    }
                })
                .catch((err) => {
                    console.log(err);
                    resolve([]);
                })
        });
    }

    getInstance(instance: string): Promise<SystemInfoModel | null> {
        let request = this.manager.createRequest({
            service: this.manager.api,
            path: '/logging/v3/instance/' + encodeURIComponent(instance),
            method: HttpMethod.GET
        });

        return new Promise<SystemInfoModel | null>((resolve, reject) => {
            this.manager
                .get<any>(request)
                .then((res) => {
                    if (res && res !== null) {
                        resolve(new SystemInfoModel(res));
                    } else {
                        resolve(null);
                    }
                })
                .catch((err) => {
                    console.log(err);
                    resolve(null);
                })
        });
    }

    getContext(contextId: string): Promise<UserContextModel | null> {
        let request = this.manager.createRequest({
            service: this.manager.api,
            path: '/logging/v3/context/' + encodeURIComponent(contextId),
            method: HttpMethod.GET
        });

        return new Promise<UserContextModel | null>((resolve, reject) => {
            this.manager
                .get<any>(request)
                .then((res) => {
                    if (res && res !== null) {
                        resolve(new UserContextModel(res));
                    } else {
                        resolve(null);
                    }
                })
                .catch((err) => {
                    console.log(err);
                    resolve(null);
                })
        });
    }

    getLog(logId: string): Promise<LogEntryModel | null> {
        let request = this.manager.createRequest({
            service: this.manager.api,
            path: '/logging/v3/log/' + encodeURIComponent(logId),
            method: HttpMethod.GET
        });

        return new Promise<LogEntryModel | null>((resolve, reject) => {
            this.manager
                .get<any>(request)
                .then((res) => {
                    if (res && res !== null) {
                        resolve(new LogEntryModel(res));
                    } else {
                        resolve(null);
                    }
                })
                .catch((err) => {
                    console.log(err);
                    resolve(null);
                })
        });
    }

    findInstances(query: SystemInfoQuery): Promise<ResultModel<SystemInfoModel>> {
        let request = this.manager.createRequest({
            service: this.manager.api,
            path: '/logging/v3/instance/find',
            method: HttpMethod.POST
        }, query);

        return new Promise<ResultModel<SystemInfoModel>>((resolve, reject) => {
            this.manager
                .get<any>(request)
                .then((res) => {
                    if (res && res !== null) {
                        resolve(new ResultModel<SystemInfoModel>(res, (dt: any) => {
                            return new SystemInfoModel(dt);
                        }));
                    } else {
                        resolve(new ResultModel<SystemInfoModel>());
                    }
                })
                .catch((err) => {
                    console.log(err);
                    resolve(new ResultModel<SystemInfoModel>());
                })
        });
    }

    findContexts(query: UserContextQuery): Promise<ResultModel<UserContextModel>> {
        let request = this.manager.createRequest({
            service: this.manager.api,
            path: '/logging/v3/context/find',
            method: HttpMethod.POST
        }, query);

        return new Promise<ResultModel<UserContextModel>>((resolve, reject) => {
            this.manager
                .get<any>(request)
                .then((res) => {
                    if (res && res !== null) {
                        resolve(new ResultModel<UserContextModel>(res, (dt: any) => {
                            return new UserContextModel(dt);
                        }));
                    } else {
                        resolve(new ResultModel<UserContextModel>());
                    }
                })
                .catch((err) => {
                    console.log(err);
                    resolve(new ResultModel<UserContextModel>());
                })
        });

    }

    private convertLogLevel(lvl: LogLevel): LogLevelForQuery {
        if (lvl == LogLevel.Critical) {
            return LogLevelForQuery.Critical;
        }
        if (lvl == LogLevel.Debug) {
            return LogLevelForQuery.Debug;
        }
        if (lvl == LogLevel.Error) {
            return LogLevelForQuery.Error;
        }
        if (lvl == LogLevel.Information) {
            return LogLevelForQuery.Information;
        }
        if (lvl == LogLevel.Trace) {
            return LogLevelForQuery.Trace;
        }
        if (lvl == LogLevel.Warning) {
            return LogLevelForQuery.Warning;
        }
        return LogLevelForQuery.None;
    }

    private convertLogLevels(lvls: LogLevel[] | undefined | null): LogLevelForQuery[] | undefined {
        if (!lvls || lvls == null) {
            return undefined;
        }

        let res = [];
        for (let i = 0; i < lvls.length; i++) {
            res.push(this.convertLogLevel(lvls[i]));
        }
        return res;
    }

    findLogs(query: LogEntryQuery): Promise<ResultModel<LogEntryModel>> {
        let qry = {} as any;
        if (query.instance) {
            qry.instance = query.instance;
        }
        if (query.contextId) {
            qry.contextId = query.contextId;
        }
        if (query.category) {
            qry.category = query.category;
        }
        if (query.levels) {
            qry.levels = this.convertLogLevels(query.levels);
        }
        if (query.dateRange) {
            qry.dateRange = query.dateRange;
        }
        if (query.lastdate) {
            qry.lastdate = query.lastdate;
        }
        if (query.lastId) {
            qry.lastId = query.lastId;
        }

        let request = this.manager.createRequest({
            service: this.manager.api,
            path: '/logging/v3/logs/find',
            method: HttpMethod.POST
        }, qry);

        return new Promise<ResultModel<LogEntryModel>>((resolve, reject) => {
            this.manager
                .get<any>(request)
                .then((res) => {
                    if (res && res !== null) {
                        resolve(new ResultModel<LogEntryModel>(res, (dt: any) => {
                            return new LogEntryModel(dt);
                        }));
                    } else {
                        resolve(new ResultModel<LogEntryModel>());
                    }
                })
                .catch((err) => {
                    console.log(err);
                    resolve(new ResultModel<LogEntryModel>());
                })
        });

    }
}

export default (framework: IFramework) => {
    framework.registerTransient<ILogData>('ILogData', (frm) => {
        return new LogData(frm);
    });
}